(() => {
  function formatMoney(cents) {
    try {
      return Shopify.formatMoney(cents);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function findVariantBySize(variants, optionIndex, sizeValue) {
    if (!Array.isArray(variants)) return null;
    return (
      variants.find((v) => v.options && v.options[optionIndex] === sizeValue) ||
      null
    );
  }

  function computeTotal(selectedVariants) {
    return selectedVariants.reduce((sum, v) => sum + (v ? v.price : 0), 0);
  }

  function updateTotal(root) {
    const selected = getSelectedVariants(root);
    const validSelected = selected.filter((v) => v !== null);
    const fixedTotalAttr = root.getAttribute("data-fixed-total");
    const fixedTotalCents = fixedTotalAttr
      ? parseInt(fixedTotalAttr, 10)
      : null;
    const totalCents =
      typeof fixedTotalCents === "number" && !isNaN(fixedTotalCents)
        ? fixedTotalCents
        : computeTotal(validSelected);
    const totalEl = root.querySelector('[data-role="bundle-total"]');
    if (totalEl) totalEl.textContent = formatMoney(totalCents);
  }

  function getSelectedVariants(root) {
    const items = [...root.querySelectorAll(".bundle-item")];
    return items.map((item, index) => {
      const optionIndex = Number(item.getAttribute("data-option-index")) || 0;
      const isColorPicker =
        item.getAttribute("data-is-color-picker") === "true";
      const checked = item.querySelector('[data-role="size-input"]:checked');
      const selectedValue = checked ? checked.value : null;
      const variantsScript = item.querySelector("script[data-variants]");
      let variants = [];
      try {
        variants = JSON.parse(variantsScript?.textContent || "[]");
      } catch (e) {}

      // If no value selected, check if item has picker
      const sizeContainer = item.querySelector(".bundle-item__size");
      const hasFieldset = item.querySelector(
        "fieldset.product-form__input--pill",
      );
      const hasPicker =
        hasFieldset && sizeContainer && sizeContainer.style.display !== "none";
      const hidePicker = item.getAttribute("data-hide-picker") === "true";
      const selectedVariantId = item.getAttribute("data-selected-variant-id");

      if (!selectedValue) {
        // If item doesn't have a picker (or picker is hidden), use selected variant from metafield or first available variant
        if ((!hasPicker || hidePicker) && variants.length > 0) {
          // If a specific variant ID is provided from metafield, use that
          if (selectedVariantId) {
            const selectedVariant = variants.find(
              (v) => v.id === Number(selectedVariantId),
            );
            if (selectedVariant) {
              return selectedVariant;
            }
          }
          // Fallback: Find first available variant, or just use first variant
          const availableVariant =
            variants.find((v) => v.available === true) || variants[0];
          return availableVariant;
        }
        return null;
      }

      // Use the selected value to find the variant
      return findVariantBySize(variants, optionIndex, selectedValue);
    });
  }

  // Track if add to cart is in progress to prevent double clicks
  let isAddingToCart = false;

  async function addBundleToCart(root) {
    // Prevent multiple simultaneous calls
    if (isAddingToCart) {
      return;
    }
    isAddingToCart = true;

    try {
      const parentId = root.getAttribute("data-bundle-parent-id");
      const fixedTotalAttr = root.getAttribute("data-fixed-total");
      const bundleSetPriceCents = fixedTotalAttr
        ? parseInt(fixedTotalAttr, 10)
        : null;
      const addAsParent = root.getAttribute("data-add-as-parent") === "true";
      const parentVariantId = root.getAttribute("data-parent-variant-id");
      const selected = getSelectedVariants(root);
      // Filter out null values (items that couldn't be selected)
      const validSelected = selected.filter((v) => v !== null);
      if (validSelected.length === 0) {
        alert("Please select all required options.");
        isAddingToCart = false;
        return;
      }
      if (validSelected.some((v) => v.available === false)) {
        alert("One or more selected items are unavailable.");
        isAddingToCart = false;
        return;
      }
      let items = [];
      if (addAsParent && parentVariantId && bundleSetPriceCents) {
        // Add only the parent line at the bundle price
        items = [
          {
            id: Number(parentVariantId),
            quantity: 1,
          },
        ];
      } else {
        // Add component lines; pricing stays sum-of-parts
        // Reverse the array so items appear in correct order in cart (cart shows newest first)
        items = validSelected
          .map((v) => ({
            id: v.id,
            quantity: 1,
          }))
          .reverse();
      }

      const res = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        alert("Could not add bundle to cart.");
        isAddingToCart = false;
        return;
      }

      // Refresh via sections API and let cart-drawer render its contents
      try {
        const cartUrl = (window.routes && window.routes.cart_url) || "/cart";
        const resp = await fetch(
          `${cartUrl}?sections=cart-drawer,cart-icon-bubble`,
        );
        const sections = await resp.json();
        // Removed visual price adjustment and bundle properties
        const drawerEl = document.querySelector("cart-drawer");
        if (drawerEl && typeof drawerEl.renderContents === "function") {
          drawerEl.renderContents({ sections });
          // Ensure header shows and empty state cleared
          drawerEl.classList.remove("is-empty");
          const headerEl = drawerEl.querySelector(".drawer__header");
          if (headerEl) {
            headerEl.style.display = "flex";
            headerEl.style.visibility = "visible";
          }
          if (typeof window.ensureCartDrawerHeaderVisible === "function") {
            window.ensureCartDrawerHeaderVisible();
          }
        } else {
          // Fallback: replace key parts manually
          const parser = new DOMParser();
          if (sections["cart-drawer"]) {
            const doc = parser.parseFromString(
              sections["cart-drawer"],
              "text/html",
            );
            const newInner = doc.querySelector("#CartDrawer");
            const oldInner = document.querySelector("#CartDrawer");
            if (newInner && oldInner) oldInner.replaceWith(newInner);
          }
          // Ensure header shows and empty state cleared
          const drawerWrap = document.querySelector("cart-drawer");
          if (drawerWrap) {
            drawerWrap.classList.remove("is-empty");
            const headerEl = drawerWrap.querySelector(".drawer__header");
            if (headerEl) {
              headerEl.style.display = "flex";
              headerEl.style.visibility = "visible";
            }
          }
          if (sections["cart-icon-bubble"]) {
            const doc2 = parser.parseFromString(
              sections["cart-icon-bubble"],
              "text/html",
            );
            const newIcon = doc2.getElementById("cart-icon-bubble");
            const oldIcon = document.getElementById("cart-icon-bubble");
            if (newIcon && oldIcon) oldIcon.replaceWith(newIcon);
          }
        }
        if (typeof window.reattachCartDrawerListeners === "function") {
          window.reattachCartDrawerListeners();
        }
        const drawer = document.querySelector("cart-drawer");
        if (drawer && typeof drawer.open === "function") drawer.open();
      } catch (e) {
        try {
          const drawer = document.querySelector("cart-drawer");
          if (drawer && typeof drawer.open === "function") drawer.open();
        } catch {}
      }
    } finally {
      // Always reset the flag when done
      isAddingToCart = false;
    }
  }

  function filterVariantsByColor(variants, colorFilter, colorOptionIndex) {
    if (
      !colorFilter ||
      colorOptionIndex === undefined ||
      colorOptionIndex < 0
    ) {
      return variants;
    }
    const targetColorLower = colorFilter.toLowerCase().trim();
    return variants.filter((v) => {
      if (!v.options || v.options.length <= colorOptionIndex) return false;
      const variantColor = (v.options[colorOptionIndex] || "")
        .toLowerCase()
        .trim();
      return variantColor === targetColorLower;
    });
  }

  function getUniqueSizes(variants, sizeOptionIndex) {
    const sizes = new Set();
    const seen = new Set();
    variants.forEach((v) => {
      if (v.options && v.options.length > sizeOptionIndex) {
        const size = v.options[sizeOptionIndex];
        if (size && !seen.has(size)) {
          sizes.add(size);
          seen.add(size);
        }
      }
    });

    // Custom sort function for sizes (S, M, L, XL, XXL, etc.)
    const sizeOrder = [
      "XXS",
      "XS",
      "S",
      "M",
      "L",
      "XL",
      "XXL",
      "XXXL",
      "4XL",
      "5XL",
    ];
    return Array.from(sizes).sort((a, b) => {
      const aIndex = sizeOrder.indexOf(a.toUpperCase());
      const bIndex = sizeOrder.indexOf(b.toUpperCase());

      // If both sizes are in the order array, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only one is in the order array, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      // If neither is in the order array, sort alphabetically
      return a.localeCompare(b);
    });
  }

  function populateSizeOptions(item) {
    const variantsScript = item.querySelector("script[data-variants]");
    if (!variantsScript) return;

    let allVariants = [];
    try {
      allVariants = JSON.parse(variantsScript.textContent || "[]");
    } catch (e) {
      return;
    }

    const colorFilter = item.getAttribute("data-color-filter");
    const colorOptionIndex = item.getAttribute("data-color-option-index");
    const sizeOptionIndex = Number(item.getAttribute("data-option-index")) || 0;
    const isColorPicker = item.getAttribute("data-is-color-picker") === "true";

    // Filter variants by color (if needed)
    let filteredVariants = allVariants;
    if (colorFilter && colorOptionIndex && !isColorPicker) {
      filteredVariants = filterVariantsByColor(
        allVariants,
        colorFilter,
        colorOptionIndex ? Number(colorOptionIndex) : -1,
      );
    }

    // Update the variants JSON with filtered variants
    variantsScript.textContent = JSON.stringify(filteredVariants);

    // Get unique options (sizes or colors) from filtered variants
    const optionIndex = isColorPicker
      ? colorOptionIndex
        ? Number(colorOptionIndex)
        : 0
      : sizeOptionIndex;
    const sizes = getUniqueSizes(filteredVariants, optionIndex);

    // Find available options (sizes or colors)
    const availableSizes = sizes.filter((optionValue) => {
      return filteredVariants.some(
        (v) =>
          v.options &&
          v.options[optionIndex] === optionValue &&
          v.available === true,
      );
    });

    // Get the fieldset - inputs should be direct children, not in a placeholder div
    const fieldset = item.querySelector("fieldset.product-form__input--pill");
    if (!fieldset) return;

    // Get legend first
    const legend = fieldset.querySelector("legend");

    // Update label if it's a color picker and remove "Find your size" button
    if (isColorPicker && legend) {
      const labelSize = legend.querySelector(".form__label-size");
      if (labelSize) {
        labelSize.textContent = "Color";
      }
      // Remove "Find your size" button if it exists
      const findSizeButton = legend.querySelector(
        ".find-size-link, .bundle-size-chart",
      );
      if (findSizeButton) {
        findSizeButton.remove();
      }
    }

    // Remove any existing size inputs
    const existingInputs = fieldset.querySelectorAll(
      '[data-role="size-input"]',
    );
    existingInputs.forEach((input) => {
      const label = fieldset.querySelector(`label[for="${input.id}"]`);
      if (label) label.remove();
      input.remove();
    });

    // Remove placeholder div if it exists
    const placeholder = fieldset.querySelector(
      '[data-role="size-options-placeholder"]',
    );
    if (placeholder) placeholder.remove();

    // Get or create the data-selected-value span in the legend
    if (legend) {
      let selectedValueSpan = legend.querySelector("[data-selected-value]");
      if (!selectedValueSpan) {
        const labelDiv = legend.querySelector(".form__label div");
        if (labelDiv) {
          selectedValueSpan = document.createElement("span");
          selectedValueSpan.setAttribute("data-selected-value", "");
          labelDiv.appendChild(selectedValueSpan);
        }
      }
    }

    const bundleRoot = item.closest(".bundle");
    const bundleId = bundleRoot?.id || "BundleRoot";
    const componentIndex = item.getAttribute("data-component-index") || "0";

    // Hide picker if no options exist
    const sizeContainer = item.querySelector(".bundle-item__size");
    if (sizes.length === 0) {
      if (sizeContainer) {
        sizeContainer.style.display = "none";
      }
      return;
    }

    // Show picker if it was hidden
    if (sizeContainer) {
      sizeContainer.style.display = "";
    }

    let firstChecked = true;
    const uniqueSizes = [...new Set(sizes)];
    const defaultSize =
      availableSizes.find((size) => uniqueSizes.includes(size)) ||
      uniqueSizes[0];

    uniqueSizes.forEach((optionValue) => {
      const optionSlug = optionValue.toLowerCase().replace(/\s+/g, "-");
      const inputId = `bundle-${bundleId}-${componentIndex}-${optionSlug}`;
      const isAvailable = filteredVariants.some(
        (v) =>
          v.options &&
          v.options[optionIndex] === optionValue &&
          v.available === true,
      );

      const input = document.createElement("input");
      input.type = "radio";
      input.id = inputId;
      input.name = `bundle-${bundleId}-${componentIndex}`;
      input.value = optionValue;
      input.setAttribute("data-role", "size-input");
      input.className = isAvailable
        ? "bundle-size-input"
        : "bundle-size-input disabled visually-disabled";
      if (firstChecked && optionValue === defaultSize) {
        input.checked = true;
        firstChecked = false;
        // Update selected value display
        const selectedValueSpan = legend?.querySelector(
          "[data-selected-value]",
        );
        if (selectedValueSpan) selectedValueSpan.textContent = optionValue;
      }

      const label = document.createElement("label");
      label.htmlFor = inputId;
      label.textContent = optionValue;
      // Add proper classes for styling
      label.className = "bundle-pill-label bundle-size-option";

      // Append directly to fieldset (not to a placeholder div)
      fieldset.appendChild(input);
      fieldset.appendChild(label);
    });

    // Add change listener to update selected value display
    fieldset.querySelectorAll('[data-role="size-input"]').forEach((input) => {
      input.addEventListener("change", function () {
        const selectedValueSpan = legend?.querySelector(
          "[data-selected-value]",
        );
        if (selectedValueSpan && this.checked) {
          selectedValueSpan.textContent = this.value;
        }
      });
    });
  }

  function init(root) {
    // Prevent duplicate initialization
    if (root.hasAttribute("data-bundle-initialized")) {
      return;
    }
    root.setAttribute("data-bundle-initialized", "true");

    // Populate size options for each bundle item
    const items = root.querySelectorAll(".bundle-item");
    items.forEach((item) => {
      populateSizeOptions(item);
    });

    const inputs = root.querySelectorAll('[data-role="size-input"]');
    inputs.forEach((input) =>
      input.addEventListener("change", () => updateTotal(root)),
    );

    const addBtn = root.querySelector('[data-role="bundle-add"]');
    if (addBtn) {
      // Remove any existing event listeners by cloning and replacing
      const newAddBtn = addBtn.cloneNode(true);
      addBtn.parentNode.replaceChild(newAddBtn, addBtn);
      // Add fresh event listener
      newAddBtn.addEventListener("click", () => addBundleToCart(root));
    }
    updateTotal(root);
  }

  // Inject CSS styles for bundle pills
  function injectBundleStyles() {
    // Check if styles already injected
    if (document.getElementById("bundle-pill-styles")) return;

    const style = document.createElement("style");
    style.id = "bundle-pill-styles";
    style.textContent = `
      .bundle-size-input {
        clip: rect(0, 0, 0, 0) !important;
        overflow: hidden !important;
        position: absolute !important;
        height: 1px !important;
        width: 1px !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      
      .bundle-pill-label,
      .bundle-size-option {
        border-radius: 40px !important;
        display: inline-block !important;
        margin-right: 6px !important;
        margin-bottom: 0 !important;
        padding: 1rem 2rem !important;
        font-size: 12px !important;
        letter-spacing: 0.1rem !important;
        line-height: 1 !important;
        text-align: center !important;
        transition: border 0.2s ease !important;
        cursor: pointer !important;
        position: relative !important;
        background-color: transparent !important;
        color: rgb(0, 0, 0) !important;
        font-weight: normal !important;
        text-decoration: none !important;
      }
      
      .bundle-pill-label:hover,
      .bundle-size-option:hover {
        border-color: rgb(0, 0, 0) !important;
      }
      
      .bundle-size-input:checked + .bundle-pill-label,
      .bundle-size-input:checked + .bundle-size-option {
        border: 1px solid #707070 !important;
      }
      
      .bundle-size-input:focus-visible + .bundle-pill-label,
      .bundle-size-input:focus-visible + .bundle-size-option {
        box-shadow: 0 0 0 0.3rem rgb(255, 255, 255), 0 0 0 0.5rem rgba(0, 0, 0, 0.55) !important;
      }
      
      .bundle-size-input:disabled + .bundle-pill-label,
      .bundle-size-input.disabled + .bundle-pill-label,
      .bundle-size-input.visually-disabled + .bundle-pill-label,
      .bundle-size-input:disabled + .bundle-size-option,
      .bundle-size-input.disabled + .bundle-size-option,
      .bundle-size-input.visually-disabled + .bundle-size-option {
        border-color: rgba(0, 0, 0, 0.1) !important;
        color: rgba(0, 0, 0, 0.6) !important;
        text-decoration: line-through !important;
      }
      
      .bundle-item__size fieldset legend {
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        padding: 16px 0 !important;
      }
      
      .bundle-item__size .form__label-size {
        font-weight: bold !important;
      }
      
      .bundle__items .bundle-item:last-child .bundle-item__size {
        padding: 20px 6px ;
      }
      
      @media screen and (max-width: 749px) {
        .bundle-pill-label,
        .bundle-size-option {
          padding-left: 1.5rem !important;
          padding-right: 1.5rem !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // Initialize when DOM is ready
  function tryInit() {
    const root = document.getElementById("BundleRoot");
    if (root) {
      injectBundleStyles(); // Inject CSS first
      init(root);
    } else {
      // Retry after a short delay in case DOM isn't ready yet
      setTimeout(tryInit, 100);
    }
  }

  // Try immediately (script might load after DOM is ready)
  tryInit();

  // Also listen for DOMContentLoaded in case script loads before DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tryInit);
  }
})();
